import React, { useState, ReactNode }from 'react';

import { View, Pressable } from 'react-native-web';
import CustomText from '../atoms/CustomText';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../utils/styling';


interface GeneralDialogProps {
  title: string;
  description: string;
  yesText: string;
  noText: string;
  children: ReactNode;
  onConfirm: () => void;
  onClose?: () => void;
}

export default function GeneralDialog({ 
  title,
  description,
  yesText="Yes",
  noText="No",
  onConfirm,
  onClose,
  children,
}: GeneralDialogProps) {
  const { isMobile } = useResponsive();
  const [open, setOpen] = useState(true);

  const handleConfirm = async () => {
    await onConfirm(); // parent function
    handleClose(); // close the dialog
  }

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose(); // notify parent that dialog should be closed
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
          padding: isMobile ? spacing.md : spacing.lg,
          width: isMobile ? 'calc(100vw - 64px)' : '100%',
          maxWidth: isMobile ? '400px' : '500px',
          minWidth: isMobile ? '280px' : '400px',
          margin: isMobile ? '16px auto' : 'auto',
        }
      }}
    >
      <DialogTitle style={{
        fontFamily: 'Nunito',
        fontSize: isMobile ? 24 : 32,
        fontWeight: '600',
        color: colors.dark,
        textAlign: 'center',
        paddingBottom: spacing.sm,
      }}>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ 
        paddingTop: 0,
      }}>
        <DialogContentText style={{
          fontFamily: 'Nunito',
          fontSize: isMobile ? 18 : 22,
          color: colors.gray[600],
          textAlign: 'center',
          marginBottom: spacing.md,
        }}>
          {description}
        </DialogContentText>
        
        <View style={{
          width: '100%',
        }}>
          {children}
        </View>
        
        <DialogActions style={{
          display: 'flex',
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: spacing.sm,
          justifyContent: 'center',
          marginTop: isMobile ? spacing.sm : spacing.md,
          paddingTop: spacing.md,
        }}>
          {noText && (
            <Pressable 
              onPress={handleClose}
              style={({ hovered }: { hovered: boolean}) => ({
                backgroundColor: hovered ? colors.gray[300] : colors.gray[200],
                paddingHorizontal: isMobile ? spacing.md : spacing.lg,
                paddingVertical: isMobile ? spacing.sm : spacing.md,
                borderRadius: 8,
                minWidth: isMobile ? 100 : 175,
                width: isMobile ? '100%' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              })}
            >
              <CustomText style={{
                color: colors.dark,
                fontSize: isMobile ? 14 : 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                {noText}
              </CustomText>
            </Pressable>
          )}
          
          <Pressable 
            onPress={handleConfirm}
            style={({ hovered }: { hovered: boolean}) => ({
              backgroundColor: hovered ? '#515A64' : colors.dark,
              paddingHorizontal: isMobile ? spacing.md : spacing.lg,
              paddingVertical: isMobile ? spacing.sm : spacing.md,
              borderRadius: 8,
              minWidth: isMobile ? 100 : 175,
              width: isMobile ? '100%' : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            })}
          >
            <CustomText style={{
              color: colors.white,
              fontSize: isMobile ? 14 : 16,
              fontWeight: '600',
              fontFamily: 'Nunito',
            }}>
              {yesText}
            </CustomText>
          </Pressable>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
